import { useContext, useMemo, useRef, useState } from 'react';
import { find } from 'lodash-es';
import {
  REGISTER_COMPONENTS_TYPES,
  registerComponentsMap
} from '@namespace/auth';
import {
  ReferenceContext,
  StepperContext,
  usePhoneCode
} from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import {
  STEP_COMPONENTS_TYPES,
  stepComponentsMap
} from './components/constants';

const numberWithoutCode = (v) => v.number.replace(v.code, '');
const makePhoneValidatorCacheKey = ({ number = '' }) => number;

const InfoForm = ({
  onStepSuccess,
  onGoBack,
  defaultCountryCode,
  classNames = {},
  isShowNeedHelp,
  ...props
}) => {
  const phoneValidationCache = useRef({});
  const defaultPhoneCode = usePhoneCode(defaultCountryCode);
  const { formClassName = '', inputWithoutLabel = '' } = classNames;
  const { registrationCountries, phoneCodes } = useContext(ReferenceContext);
  const countriesOptions = useMemo(
    () =>
      registrationCountries.map(({ countryName, iso1Code }) => ({
        name: countryName,
        value: iso1Code
      })),
    [registrationCountries]
  );
  const {
    stepper: {
      formData: {
        country = '',
        firstName = '',
        lastName = '',
        middleName = '',
        gender = 'mr',
        dateOfBirth = '',
        phoneNumber = { isoCode: defaultCountryCode, number: defaultPhoneCode },
        pin = '',
        city = '',
        address = '',
        zip = '',
        username = '',
        citizenship = '',
        document = '',
        documentDate = '',
        documentNumber = '',
        documentPlace = '',
        iban = '',
        documentCountry = 'HR'
      },
      errorFields = []
    }
  } = useContext(StepperContext);
  const { t, f } = useI18n();
  const DATE_FORMATS_TYPES = f.getFormats();
  const [documentType, setDocumentType] = useState(0);

  const providedCountry = country || defaultCountryCode;
  const defaultFormCountry = providedCountry
    ? find(countriesOptions, ['value', providedCountry])
    : countriesOptions[0];
  const defaultValues = {
    country: defaultFormCountry?.value,
    firstName,
    lastName,
    middleName,
    dateOfBirth,
    phoneNumber,
    gender,
    pin,
    city,
    address,
    zip,
    username,
    document,
    documentDate,
    documentPlace,
    documentNumber,
    documentCountry,
    citizenship,
    iban
  };

  const componentsList = useMemo(
    () => [
      {
        type: FORM_COMPONENTS_TYPES.REG_COUNTRY_SELECT,
        name: 'country',
        label: 'register.steps.personalInfo.selectCountry',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'username',
        label: 'register.steps.personalInfo.username',
        placeholder: 'register.steps.personalInfo.username',
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.NAME_INPUT,
        name: 'firstName',
        placeholder: 'register.steps.personalInfo.firstName',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.NAME_INPUT,
        name: 'lastName',
        placeholder: 'register.steps.personalInfo.lastName',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.REG_DATE_PICKER,
        label: 'register.steps.personalInfo.birthdate',
        name: 'dateOfBirth',
        placeholder: 'register.steps.personalInfo.birthdate',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.GENDER_BUTTONS_GROUP,
        name: 'gender',
        label: 'register.steps.personalInfo.gender',
        elements: [
          {
            label: 'register.steps.personalInfo.man',
            value: 'mr',
            iconName: 'icons/general/item/men'
          },
          {
            label: 'register.steps.personalInfo.woman',
            value: 'ms',
            iconName: 'icons/general/item/woman'
          }
        ],
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED]
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'citizenship',
        placeholder: 'register.steps.personalInfo.citizenship',
        label: 'register.steps.personalInfo.citizenship',
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [30]
          }
        ],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'city',
        label: 'register.steps.personalInfo.address',
        placeholder: 'register.steps.personalInfo.city',
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'address',
        placeholder: 'register.steps.personalInfo.address',
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'zip',
        placeholder: 'register.steps.personalInfo.zip',
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.ZIP_CODE,
          FORM_VALIDATION_TYPES.NUMBER_FORMAT_VALIDATOR,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [5]
          },
          {
            type: FORM_VALIDATION_TYPES.MIN_LENGTH_VALIDATOR,
            parameters: [5]
          }
        ],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.REG_PHONE,
        name: 'phoneNumber',
        label: 'register.steps.personalInfo.mobile',
        validationTypes: [
          {
            type: FORM_VALIDATION_TYPES.PHONE_COMPLEX,
            parameters: [
              { countries: phoneCodes },
              { minLength: 8, maxLength: 15, required: true }
            ]
          },
          {
            type: FORM_VALIDATION_TYPES.PHONE_WITH_CODE,
            parameters: [
              (v) => ({
                ...v,
                number: numberWithoutCode(v)
              }),
              {
                cache: phoneValidationCache.current,
                generateCacheKey: makePhoneValidatorCacheKey
              }
            ]
          }
        ],
        size: 'm',
        maskChar: '_'
      },
      {
        type: FORM_COMPONENTS_TYPES.SELECT,
        name: 'document',
        label: 'register.steps.personalInfo.document',
        placeholder: 'register.steps.personalInfo.document',
        options: [
          { value: 1, name: t('register.steps.personalInfo.passport') },
          { value: 3, name: t('register.steps.personalInfo.identification') }
        ],
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: STEP_COMPONENTS_TYPES.DOCUMENT_NUMBER_FIELD,
        name: 'documentNumber',
        placeholder: 'register.steps.personalInfo.documentNumber',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.DATE_PICKER,
        name: 'documentDate',
        placeholder: 'register.steps.personalInfo.documentDate',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'documentPlace',
        label: 'register.steps.personalInfo.documentPlace',
        placeholder: 'register.steps.personalInfo.documentPlace',
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [230]
          }
        ],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.PIN_CODE_INPUT,
        name: 'pin',
        label: 'register.steps.personalInfo.oib',
        placeholder: 'register.steps.personalInfo.oib',
        validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'iban',
        label: 'register.steps.personalInfo.iban',
        placeholder: 'register.steps.personalInfo.iban',
        enableTrimSpaces: true,
        validationTypes: [
          FORM_VALIDATION_TYPES.IBAN_ASYNC,
          FORM_VALIDATION_TYPES.REQUIRED
        ],
        size: 'm'
      },
      {
        type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
        value: 'register.steps.continue',
        size: 'xl'
      },
      {
        type: REGISTER_COMPONENTS_TYPES.BACK_BUTTON,
        onGoBack
      }
    ],
    [t]
  );

  if (isShowNeedHelp) {
    componentsList.push({
      type: FORM_COMPONENTS_TYPES.NEED_HELP,
      needHelpText: 'web.register.needhelp.link',
      dataRole: 'register-need-help'
    });
  }

  const extendedComponentsList = useMemo(
    () => ({
      username: {
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.USERNAME_ASYNC,
          {
            type: FORM_VALIDATION_TYPES.REGEXP_VALIDATOR,
            parameters: [
              /^[A-Za-z0-9]{3,20}$/,
              'register.steps.personalInfo.usernamePattern'
            ]
          }
        ]
      },
      firstName: {
        classNames: {
          wrapperClassName: inputWithoutLabel
        }
      },
      lastName: {
        classNames: {
          wrapperClassName: inputWithoutLabel
        }
      },
      dateOfBirth: {
        classNames: {
          datePickerWrapperClassName: ''
        },
        format: DATE_FORMATS_TYPES.CLIENT_API
      },
      city: {
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.CITY,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [40]
          }
        ]
      },
      address: {
        classNames: {
          wrapperClassName: inputWithoutLabel
        },
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.ADDRESS,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [150]
          }
        ]
      },
      zip: {
        classNames: {
          wrapperClassName: inputWithoutLabel
        }
      },
      document: {
        onChange: setDocumentType
      },
      documentNumber: {
        disabled: !documentType,
        classNames: {
          wrapperClassName: inputWithoutLabel
        }
      },
      documentDate: {
        disabled: !documentType,
        timeRange: {
          from: f.getDateTime(),
          to: f.getDateTime().plus({ year: 60 })
        },
        classNames: {
          datePickerContainerClassName: inputWithoutLabel,
          datePickerWrapperClassName: ''
        }
      },
      documentPlace: {
        disabled: !documentType
      }
    }),
    [inputWithoutLabel, DATE_FORMATS_TYPES.CLIENT_API, documentType, f]
  );

  const modifiedComponentsList = useMemo(
    () =>
      componentsList.map((item) => ({
        ...item,
        ...extendedComponentsList[item.name],
        customError: errorFields?.find((el) => el.key === item.name)
          ? `register.error.${
              errorFields.find((el) => el.key === item.name).errorCode
            }`
          : ''
      })),
    [componentsList, errorFields, extendedComponentsList]
  );

  return (
    <FormBuilder
      wrapperClassName={formClassName}
      componentsMap={{ ...registerComponentsMap, ...stepComponentsMap }}
      components={modifiedComponentsList}
      formConfig={{ defaultValues }}
      onSubmit={onStepSuccess}
      formProps={{
        defaultCountryCode: defaultFormCountry || 'none',
        isShowPhoneMask: true,
        ...props
      }}
    />
  );
};

export default InfoForm;
